import React, { useState, useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";
import heroImg1 from "../assets/images/hero-image-1.jpg";
import heroImg2 from "../assets/images/hero-image-2.jpg";
import heroImg4 from "../assets/images/hero-image-4.jpg";

const sliderImages = [heroImg1, heroImg2, heroImg4];
const CONSTRUCTION_DASHBOARD_URL = "https://universal-construction-dashboard.onrender.com";

// ---------- Styled Components ----------
const HeroContainer = styled.section`
  --bg1: #f6f8fb;
  --bg2: #eef1f5;
  position: relative;
  display: flex;
  align-items: center;
  padding: 7.5rem 1.5rem 4rem;
  background: radial-gradient(1200px 800px at 10% 10%, #ffffff 0%, transparent 60%),
              radial-gradient(1200px 800px at 90% 0%, rgba(61, 114, 148, 0.15) 0%, transparent 55%),
              linear-gradient(135deg, var(--bg1), var(--bg2));
  @media (max-width: 1024px) { padding: 7rem 1.25rem 3.25rem; }
  @media (max-width: 768px) { padding: 6rem 1rem 3rem; }
  @media (max-width: 480px) { padding: 5rem 0.8rem 2.4rem; }
`;

const Noise = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: 0.05;
  background-image: radial-gradient(1px 1px at 10px 10px, #000 1px, transparent 0);
  background-size: 12px 12px;
  mix-blend-mode: multiply;
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1.3fr;
  gap: clamp(1.25rem, 2.5vw, 3rem);
  align-items: center;
  @media (max-width: 900px) { grid-template-columns: 1fr; text-align: center; }
`;

const TextContent = styled.div` padding: 0.5rem 0; `;
const MainHeading = styled.h1`
  font-weight: 800;
  color: #111827;
  line-height: 1.1;
  margin: 0 0 0.75rem;
  font-size: clamp(1.9rem, 3vw, 3rem);
  white-space: nowrap;
`;
const SubHeading = styled.p`
  color: #44525f;
  margin: 0 0 1.25rem;
  font-size: clamp(0.95rem, 1.4vw, 1.05rem);
`;
const CTAGroup = styled.div`
  display: inline-flex;
  gap: 0.75rem;
  margin: 1.25rem 0 0;
  @media (max-width: 900px) { justify-content: center; width: 100%; flex-wrap: wrap; }
`;
const PrimaryCTA = styled.a`
  background: linear-gradient(45deg, #ff914d, #ff6b35);
  color: #fff;
  text-decoration: none;
  font-weight: 700;
  padding: 0.9rem 1.2rem;
  border-radius: 12px;
  box-shadow: 0 10px 24px rgba(255, 145, 77, 0.3);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  &:hover { transform: translateY(-2px); box-shadow: 0 14px 28px rgba(255,145,77,0.38); }
  &:active { transform: translateY(0); }
`;
const GhostCTA = styled.a`
  color: #2a5a7a;
  text-decoration: none;
  font-weight: 700;
  padding: 0.9rem 1.1rem;
  border-radius: 12px;
  border: 1px solid rgba(42,90,122,0.25);
  background: rgba(42,90,122,0.06);
  transition: background 0.2s ease, transform 0.2s ease;
  &:hover { background: rgba(42,90,122,0.12); transform: translateY(-2px); }
`;
const DashboardCTA = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: linear-gradient(45deg, #3dccc7, #2a8f89);
  color: #fff;
  text-decoration: none;
  font-weight: 700;
  padding: 0.9rem 1.2rem;
  border-radius: 12px;
  box-shadow: 0 10px 24px rgba(61, 204, 199, 0.3);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  &:hover { transform: translateY(-2px); box-shadow: 0 14px 28px rgba(61,204,199,0.38); }
  &:active { transform: translateY(0); }
`;

const Badges = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 1.25rem;
  flex-wrap: wrap;
  @media (max-width: 900px) { justify-content: center; }
`;
const Badge = styled.span`
  font-size: 0.85rem;
  color: #0f172a;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  padding: 0.45rem 0.75rem;
`;

const glow = keyframes`
  0% { box-shadow: 0 15px 40px rgba(42,90,122,0.18), inset 0 0 0 1px rgba(255,255,255,0.06); }
  50% { box-shadow: 0 18px 46px rgba(42,90,122,0.26), inset 0 0 0 1px rgba(255,255,255,0.1); }
  100% { box-shadow: 0 15px 40px rgba(42,90,122,0.18), inset 0 0 0 1px rgba(255,255,255,0.06); }
`;

const ImageContainer = styled.div` position: relative; width: 100%; max-width: 1000px; margin: 0 auto; `;
const CardFrame = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16/10;
  border-radius: 20px;
  overflow: hidden;
  background: linear-gradient(135deg, #3dccc7 0%, #2a5a7a 100%);
  animation: ${glow} 6s ease-in-out infinite;
  @media (max-width: 1024px) { border-radius: 16px; }
  @media (max-width: 768px) { border-radius: 14px; max-width: 100%; }
  @media (max-width: 480px) { border-radius: 12px; }
`;

const SliderWrapper = styled.div`
  position: absolute;
  inset: 1px;
  overflow: hidden;
  border-radius: inherit;
`;
const Slides = styled.div`
  display: flex;
  height: 100%;
  transition: transform 0.8s ease;
`;
const Slide = styled.div`
  min-width: 100%;
  height: 100%;
  img { width: 100%; height: 100%; object-fit: cover; }
`;

const NavButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 5;
  background: rgba(42, 90, 122, 0.5);
  color: #fff;
  border: none;
  border-radius: 50%;
  width: 38px;
  height: 38px;
  font-size: 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;
  &:hover { background: rgba(42,90,122,0.8); }
`;
const LeftButton = styled(NavButton)` left: 12px; `;
const RightButton = styled(NavButton)` right: 12px; `;

const float = keyframes`0%{transform:translateY(0);opacity:.8}50%{transform:translateY(-8px);opacity:1}100%{transform:translateY(0);opacity:.8}`;
const Sparkles = styled.div` position: absolute; inset: -6% -4% -6% -4%; pointer-events: none; `;
const Sparkle = styled.span`
  position: absolute;
  width: ${(p) => p.size || 10}px;
  height: ${(p) => p.size || 10}px;
  border-radius: 50%;
  background: radial-gradient(circle at 30% 30%, #fff, rgba(255,255,255,0.35) 60%, transparent 70%);
  box-shadow: 0 0 18px rgba(255,255,255,0.35);
  left: ${(p) => p.left || 0}%;
  top: ${(p) => p.top || 0}%;
  animation: ${float} ${(p) => p.dur || 3.5}s ease-in-out infinite;
  animation-delay: ${(p) => p.delay || 0}s;
  opacity: 0.9;
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
  margin-top: 1.25rem;
  max-width: 420px;
  @media (max-width: 900px) { margin-left: auto; margin-right: auto; }
  @media (max-width: 480px) { grid-template-columns: 1fr; max-width: 260px; }
`;
const StatCard = styled.div`
  background: #fff;
  border: 1px solid #eef2f7;
  border-radius: 14px;
  padding: 0.9rem 1rem;
  text-align: left;
  min-width: 0;
  @media (max-width: 900px) { text-align: center; }
`;
const StatNumber = styled.div`
  font-weight: 800;
  color: #2a5a7a;
  font-size: clamp(1.35rem, 2vw, 1.7rem);
  letter-spacing: -0.01em;
`;
const StatLabel = styled.div`
  color: #667482;
  font-size: 0.9rem;
  margin-top: 0.25rem;
`;

// ---------- Hero Section Component ----------
const HeroSection = () => {
  const [index, setIndex] = useState(1); // start from first real slide
  const [transitioning, setTransitioning] = useState(true);
  const sliderRef = useRef(null);

  // Clone first and last slides for infinite loop
  const slides = [sliderImages[sliderImages.length - 1], ...sliderImages, sliderImages[0]];

  const goNext = () => {
    setIndex(prev => prev + 1);
    setTransitioning(true);
  };
  const goPrev = () => {
    setIndex(prev => prev - 1);
    setTransitioning(true);
  };

  useEffect(() => {
    let interval = setInterval(goNext, 6000);
    const sliderNode = sliderRef.current;

    const pause = () => clearInterval(interval);
    const resume = () => interval = setInterval(goNext, 6000);

    sliderNode.addEventListener("mouseenter", pause);
    sliderNode.addEventListener("mouseleave", resume);

    return () => {
      clearInterval(interval);
      sliderNode.removeEventListener("mouseenter", pause);
      sliderNode.removeEventListener("mouseleave", resume);
    };
  }, []);

  // Handle infinite loop jump
  const handleTransitionEnd = () => {
    if (index === 0) {
      setTransitioning(false);
      setIndex(sliderImages.length);
    } else if (index === sliderImages.length + 1) {
      setTransitioning(false);
      setIndex(1);
    }
  };

  return (
    <HeroContainer>
      <Noise />
      <HeroContent>
        <TextContent>
          <MainHeading>Build Smarter with AI</MainHeading>
          <SubHeading>
            Discover how construction professionals are using AI to save hours, cut costs, and improve project workflows.
          </SubHeading>
          <CTAGroup>
            <PrimaryCTA href="/ebook">Get the Free eBook</PrimaryCTA>
            <GhostCTA href="/prompt-gallery">Explore Prompt Gallery</GhostCTA>
            <DashboardCTA href={CONSTRUCTION_DASHBOARD_URL} target="_blank" rel="noopener noreferrer">
              Explore Construction Dashboard ↗
            </DashboardCTA>
          </CTAGroup>
          <Badges>
            <Badge>🔒 Private & secure tips</Badge>
            <Badge>📈 Proven time-savers</Badge>
          </Badges>
          <Stats>
            <StatCard>
              <StatNumber>50%</StatNumber>
              <StatLabel>Time Saved</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>95%</StatNumber>
              <StatLabel>Accuracy Rate</StatLabel>
            </StatCard>
            {/* <StatCard>
              <StatNumber>10K+</StatNumber>
              <StatLabel>Projects Managed</StatLabel>
            </StatCard> */}
          </Stats>
        </TextContent>
        <ImageContainer ref={sliderRef}>
          <CardFrame>
            <SliderWrapper>
              <Slides
                style={{
                  transform: `translateX(-${index * 100}%)`,
                  transition: transitioning ? "transform 0.8s ease" : "none"
                }}
                onTransitionEnd={handleTransitionEnd}
              >
                {slides.map((img, i) => (
                  <Slide key={i}><img src={img} alt={`Slide ${i}`} /></Slide>
                ))}
              </Slides>
            </SliderWrapper>
            <LeftButton onClick={goPrev}>&lt;</LeftButton>
            <RightButton onClick={goNext}>&gt;</RightButton>
            <Sparkles aria-hidden>
              <Sparkle left={12} top={18} size={10} dur={3.8} delay={0.2} />
              <Sparkle left={22} top={72} size={8} dur={4.2} delay={1.1} />
              <Sparkle left={38} top={28} size={12} dur={3.6} delay={0.6} />
              <Sparkle left={58} top={62} size={9} dur={4.0} delay={0.4} />
              <Sparkle left={74} top={34} size={11} dur={3.7} delay={1.4} />
              <Sparkle left={86} top={18} size={8} dur={4.1} delay={0.9} />
            </Sparkles>
          </CardFrame>
        </ImageContainer>
      </HeroContent>
    </HeroContainer>
  );
};

export default HeroSection;
